import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { SectionHeading, SectionLabel } from "./Section";

const items = [
  { year: "2024", title: "Foundation", body: "Focused on academic excellence and building a deep base in problem solving." },
  { year: "2025", title: "Breakthrough", body: "Scored 94.29 percentile in JEE. Developed strong analytical thinking and self-learning discipline." },
  { year: "2026", title: "Exploration", body: "Started deep exploration of AI tools, automation systems, software engineering, and startup ecosystems." },
  { year: "Future", title: "Building", body: "Building SaaS products that solve meaningful problems at scale." },
];

export function Timeline() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 30%"] });
  const height = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="timeline" className="relative py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-16 max-w-2xl">
          <SectionLabel>Timeline</SectionLabel>
          <SectionHeading>A trajectory in motion.</SectionHeading>
        </div>

        <div ref={ref} className="relative mx-auto max-w-3xl">
          <div className="absolute left-4 top-0 h-full w-px bg-white/10 md:left-1/2 md:-translate-x-1/2" />
          <motion.div
            style={{ height }}
            className="absolute left-4 top-0 w-px bg-gradient-to-b from-electric via-electric/60 to-transparent shadow-[0_0_12px_var(--electric)] md:left-1/2 md:-translate-x-1/2"
          />

          <div className="space-y-16">
            {items.map((it, i) => (
              <motion.div
                key={it.year}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className={`relative grid grid-cols-[40px_1fr] items-start gap-6 md:grid-cols-2 md:gap-12 ${
                  i % 2 === 0 ? "" : "md:[&>*:first-child]:order-2"
                }`}
              >
                <div className={`hidden md:block ${i % 2 === 0 ? "text-right" : ""}`}>
                  {i % 2 === 0 && <TimelineCard {...it} align="right" />}
                  {i % 2 !== 0 && <div className="text-sm text-muted-foreground">{it.year}</div>}
                </div>

                <div className="absolute left-4 top-2 -translate-x-1/2 md:left-1/2">
                  <div className="relative flex h-3 w-3 items-center justify-center">
                    <div className="absolute h-3 w-3 rounded-full bg-electric shadow-[0_0_16px_var(--electric)]" />
                    <div className="h-5 w-5 rounded-full border border-electric/40" />
                  </div>
                </div>

                <div className="pl-10 md:pl-0">
                  {i % 2 === 0 ? (
                    <div className="md:hidden">
                      <TimelineCard {...it} />
                    </div>
                  ) : (
                    <TimelineCard {...it} />
                  )}
                  <div className="hidden md:block">
                    {i % 2 === 0 ? (
                      <div className="text-sm text-muted-foreground">{it.year}</div>
                    ) : (
                      <TimelineCard {...it} />
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TimelineCard({
  year,
  title,
  body,
  align,
}: {
  year: string;
  title: string;
  body: string;
  align?: "right";
}) {
  return (
    <div className={`glass rounded-2xl p-6 ${align === "right" ? "text-left md:text-right" : ""}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-electric">
        {align === "right" ? (
          <>
            <span className="ml-auto">{year}</span>
          </>
        ) : (
          <span>{year}</span>
        )}
      </div>
      <div className="mt-2 text-xl font-semibold tracking-tight">{title}</div>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
